import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Globe2, Layers } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const EXPERTISE = [
  "Metals, Ores & Minerals",
  "Steel Plants & Ferro Alloys",
  "Biomass Energy",
  "Waste Valorization",
  "Renewable Generation",
  "Energy Storage Systems",
  "Water Treatment Plants",
  "Air Treatment & Pollution Control",
];

const Founder = () => (
  <div data-testid="founder-page" className="pt-28 sm:pt-36">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-5 gap-12 items-start">
        <Reveal className="lg:col-span-2">
          <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-lime/10 via-carbon to-obsidian p-10 flex flex-col items-center text-center overflow-hidden lg:sticky lg:top-28">
            <img
              src="/assets/logo.jpeg"
              alt="Green Planet Technologies logo"
              data-testid="founder-logo"
              className="logo-spin h-52 w-52 sm:h-64 sm:w-64 object-contain"
            />
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-lime">Promoter & Founder</p>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-white mt-1">Suresh C. Mirchandani</h1>
          </div>
        </Reveal>

        <div className="lg:col-span-3">
          <Reveal>
            <Eyebrow>The founder</Eyebrow>
            <blockquote className="mt-6 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-snug text-white">
              "Decarbonization is not a constraint on industry — it is the
              <span className="text-gradient"> greatest engineering opportunity </span>
              of our generation."
            </blockquote>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-8 text-gray-300 leading-relaxed text-base sm:text-lg">
              Suresh C. Mirchandani brings a formidable footprint across metals, ores, minerals and
              steel, extended into biomass energy, industrial waste streams, renewable energy
              generation & storage, and water & air treatment plants. That end-to-end command of
              heavy industry is the foundation of Green Planet Technologies.
            </p>
            <p className="mt-4 text-gray-400 leading-relaxed">
              His leadership philosophy: drive global industrial decarbonization through turnkey
              engineering and circular economic design — taking every project from concept to
              creation, installation, commissioning and operation.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-lime flex items-center gap-2">
                <Layers size={14} /> Domains of expertise
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                {EXPERTISE.map((e, i) => (
                  <span
                    key={e}
                    data-testid={`expertise-chip-${i + 1}`}
                    className="rounded-full border border-white/15 bg-carbon px-4 py-2 text-sm text-gray-200 hover:border-lime/50 hover:text-lime transition-colors duration-300"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <Reveal delay={0.18}>
              <div data-testid="founder-highlight-1" className="rounded-2xl border border-white/10 bg-steel p-6">
                <Award className="text-lime" size={22} />
                <p className="mt-4 font-display font-bold text-lg text-white">Turnkey Authority</p>
                <p className="mt-2 text-sm text-gray-400">
                  A single accountable leader across design, build, commissioning and operation.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.24}>
              <div data-testid="founder-highlight-2" className="rounded-2xl border border-white/10 bg-steel p-6">
                <Globe2 className="text-lime" size={22} />
                <p className="mt-4 font-display font-bold text-lg text-white">Global Outlook</p>
                <p className="mt-2 text-sm text-gray-400">
                  Circular economy projects engineered for deployment across geographies.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.26}>
            <div data-testid="founder-seal" className="mt-6 flex items-center gap-4 rounded-2xl border border-white/10 bg-steel p-5">
              <img
                src="/assets/logo.jpeg"
                alt="Green Planet Technologies logo"
                className="logo-spin h-14 w-14 object-contain"
              />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-lime">Signatory</p>
                <p className="font-display font-bold text-lg text-white mt-1">Suresh C. Mirchandani</p>
                <p className="text-xs text-gray-400 mt-0.5">Promoter & Founder, Green Planet Technologies Pty Ltd</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.28}>
            <Link
              to="/contact"
              data-testid="founder-cta-link"
              className="mt-10 inline-flex items-center gap-2 bg-lime text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-lime-bright transition-all duration-300"
            >
              Discuss a project <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
    <div className="h-24" />
  </div>
);

export default Founder;
